"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TextInputType = void 0;
var classnames_1 = __importDefault(require("classnames"));
var react_1 = __importDefault(require("react"));
var StandardIconUtils_1 = require("../../utils/StandardIconUtils");
var Icon_1 = __importDefault(require("../icon/Icon"));
var FormComponentUtils_1 = require("./common/FormComponentUtils");
var HookUtils_1 = require("../../common/HookUtils");
var ValidationMessage_1 = __importDefault(require("./common/ValidationMessage"));
var VisuallyHidden_1 = __importDefault(require("../common/VisuallyHidden"));
var FieldValidationState_1 = require("./common/FieldValidationState");
var DeprecationUtils_1 = require("../../utils/DeprecationUtils");
var TextInputType;
(function (TextInputType) {
    TextInputType["EMAIL"] = "email";
    TextInputType["FILE"] = "file";
    TextInputType["HIDDEN"] = "hidden";
    TextInputType["NUMBER"] = "number";
    TextInputType["PASSWORD"] = "password";
    TextInputType["TEL"] = "tel";
    TextInputType["TEXT"] = "text";
})(TextInputType = exports.TextInputType || (exports.TextInputType = {}));
var Input = function (props) {
    var disabled = props.disabled, floatLabel = props.floatLabel, iconName = props.iconName, formControlProps = props.formControlProps, invalid = props.invalid, label = props.label, name = props.name, onBlur = props.onBlur, onChange = props.onChange, onClick = props.onClick, onFocus = props.onFocus, onKeyUp = props.onKeyUp, parentRef = props.parentRef, placeholder = props.placeholder, title = props.title, type = props.type, validationMessage = props.validationMessage, validationState = props.validationState, value = props.value, readOnly = props.readOnly, min = props.min, max = props.max, visuallyHiddenLabel = props.visuallyHiddenLabel, maxDigits = props.maxDigits, secondary = props.secondary;
    var elementModel = {
        disabled: disabled, floatLabel: floatLabel, iconName: iconName, label: label, name: name, onBlur: onBlur, onChange: onChange, onClick: onClick, onFocus: onFocus, onKeyUp: onKeyUp, formControlProps: formControlProps,
        parentRef: parentRef, placeholder: placeholder, title: title, type: type, value: value, readOnly: readOnly, secondary: secondary, min: min, max: max, visuallyHiddenLabel: visuallyHiddenLabel
    };
    if (placeholder && floatLabel) {
        DeprecationUtils_1.logDeprecatedPropCombination({
            propCombination: ['placeholder', 'floatLabel'],
            component: 'Input',
            seeMsg: 'The use of a placeholder with a floated label is no longer a valid configuration of the system. Use either placeholder or floatLabel alone and not in conjunction.',
            versionStart: '1.7.24',
            versionEnd: '2.0.0'
        });
    }
    return (react_1.default.createElement("label", { "data-max-digits": maxDigits, className: getClassNames(props) },
        getElements(elementModel),
        react_1.default.createElement(ValidationMessage_1.default, { validationMessage: validationMessage, validationState: invalid ? FieldValidationState_1.FieldValidationState.INVALID : validationState })));
};
Input.defaultProps = {
    placeholder: ' ',
    type: TextInputType.TEXT,
    value: ''
};
var isValidIconName = function (iconName) {
    return StandardIconUtils_1.isStandardIconNameValid(iconName);
};
var getClassNames = function (props) {
    var disabled = props.disabled, floatLabel = props.floatLabel, fullWidth = props.fullWidth, iconName = props.iconName, invalid = props.invalid, placeholder = props.placeholder, classes = props.classes, visuallyHiddenLabel = props.visuallyHiddenLabel, validationState = props.validationState;
    return classnames_1.default('fds-field', {
        'is-disabled': disabled,
        'has-floated-label': floatLabel,
        'has-icon': isValidIconName(iconName),
        'has-no-placeholder': placeholder && placeholder === ' ',
        'fds-form-control-full-width': fullWidth,
        'visually-hidden-label': visuallyHiddenLabel,
        invalid: invalid || validationState === FieldValidationState_1.FieldValidationState.INVALID,
        valid: validationState === FieldValidationState_1.FieldValidationState.VALID
    }, classes);
};
var getElements = function (props) {
    var floatLabel = props.floatLabel, label = props.label, placeholder = props.placeholder, secondary = props.secondary, visuallyHiddenLabel = props.visuallyHiddenLabel;
    var secondaryLabelEnabled = !floatLabel && secondary;
    if (floatLabel) {
        return (react_1.default.createElement(react_1.default.Fragment, null,
            getInput(props),
            getLabel(label, placeholder, secondaryLabelEnabled, visuallyHiddenLabel)));
    }
    return (react_1.default.createElement(react_1.default.Fragment, null,
        getLabel(label, placeholder, secondaryLabelEnabled, visuallyHiddenLabel),
        getInput(props)));
};
var getLabel = function (label, placeholder, secondary, visuallyHiddenLabel) {
    var placeholderLabel = (placeholder && placeholder !== ' ') && react_1.default.createElement(VisuallyHidden_1.default, null, placeholder);
    var labelContent = react_1.default.createElement(react_1.default.Fragment, null,
        label,
        " ",
        placeholderLabel);
    if (label) {
        return (react_1.default.createElement("span", { className: classnames_1.default('fds-field-label', { secondary: secondary }) }, visuallyHiddenLabel ? react_1.default.createElement(VisuallyHidden_1.default, null, labelContent) : labelContent));
    }
    return null;
};
var getInput = function (props) {
    var disabled = props.disabled, iconName = props.iconName, name = props.name, onBlur = props.onBlur, onChange = props.onChange, onFocus = props.onFocus, onKeyUp = props.onKeyUp, placeholder = props.placeholder, parentRef = props.parentRef, readOnly = props.readOnly, title = props.title, type = props.type, value = props.value, onClick = props.onClick, formControlProps = props.formControlProps;
    var minValue = props.type === TextInputType.NUMBER ? props.min : null;
    var maxValue = props.type === TextInputType.NUMBER ? props.max : null;
    var _a = HookUtils_1.useStateWithSingleProp(value), tempValue = _a[0], setTempValue = _a[1]; //eslint-disable-line react-hooks/rules-of-hooks
    var restrictedProps = [
        'className', 'disabled', 'max', 'min', 'name', 'onBlur', 'onChange', 'onClick', 'onFocus',
        'onKeyDown', 'onKeyUp', 'placeholder', 'readOnly', 'ref', 'title', 'type', 'value'
    ];
    var attributes = FormComponentUtils_1.getFormControlAttributes(formControlProps, restrictedProps);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement("input", __assign({}, attributes, { className: "fds-field-input", disabled: disabled, max: maxValue, min: minValue, name: name, onBlur: onBlur, onChange: function (evt) { return FormComponentUtils_1.handleInputValueChange(evt, setTempValue, onChange); }, onClick: onClick, onFocus: onFocus, onKeyUp: onKeyUp, placeholder: placeholder, readOnly: readOnly, ref: parentRef, title: title, type: type, value: tempValue })),
        getIcon(iconName)));
};
var getIcon = function (iconName) {
    if (isValidIconName(iconName)) {
        return react_1.default.createElement(Icon_1.default, { name: iconName, classes: "fds-field-icon" });
    }
    return null;
};
exports.default = Input;
